/**
 * Чистая механика входа через платформу: state, адрес согласия, разбор возврата.
 *
 * Вынесена из компонентов, потому что здесь живут решения, за которые платят
 * безопасностью, а не версткой:
 *
 * - **`state`** генерируется криптостойко и хранится в `sessionStorage`. Он
 *   привязывает ответ платформы к ТОМУ ЖЕ окну, которое начало вход: без сверки
 *   чужая ссылка на наш callback подсунула бы пользователю свой код.
 * - **Код приезжает во fragment** (`#sso_code=…`), а не в query. Fragment не уходит
 *   на сервер и не оседает ни в логах nginx, ни в Referer — так его отдаёт платформа
 *   (`api-new/core/services/v1/sso/lib/ssoHelpers.js:31-33`), и читать его надо
 *   оттуда же.
 * - **`redirect_uri` собирается из origin окна**, а не из сборки: один образ живёт на
 *   трёх контурах. Платформа сверяет значение ПРЕФИКСОМ со своим allowlist'ом
 *   (`ssoHelpers.js:16-28`), поэтому путь обязан совпадать с заявленным в реестре
 *   ровно (`docs/mf/PLATFORM-REGISTRY-REQUEST.md`).
 */

export const SSO_STATE_KEY = 'idea-calc-sso-state';
export const SSO_CALLBACK_PATH = '/auth/platform/callback';

/**
 * `state` входа MCP-клиента: `mcp.<txn>`. Его выдаёт сервер авторизации MCP, а не
 * эта вкладка, поэтому в `sessionStorage` его нет и сверять его здесь не с чем:
 * браузер привязан к входу cookie, которую ставит экран согласия, и её проверяет
 * бэкенд. Точка не встречается в `createState()` (base64url), так что обычный вход
 * с MCP-входом не спутать.
 */
export const MCP_STATE_PREFIX = 'mcp.';

/** Длина `state` на стороне платформы — минимум 8 символов (схема `/sso/grant`). */
const STATE_BYTES = 16;

export interface SsoCallbackParams {
  code: string;
  state: string;
}

/** Возврат MCP-входа: ровно одно из `code` / `error` — так его ждёт бэкенд. */
export type McpCallbackParams =
  | { txn: string; code: string; error?: undefined }
  | { txn: string; error: string; code?: undefined };

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function createState(): string {
  const bytes = new Uint8Array(STATE_BYTES);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

/** Путь модуля с учётом базы сборки — тот же приём, что у ссылки «Поделиться». */
export function callbackUrl(origin: string): string {
  const base = (import.meta.env.VITE_BASE_PATH || '/').replace(/\/+$/, '');
  return `${origin}${base}${SSO_CALLBACK_PATH}`;
}

export function buildAuthorizeUrl(params: {
  authorizeUrl: string;
  client: string;
  redirectUri: string;
  state: string;
}): string {
  const query = new URLSearchParams({
    client: params.client,
    redirect_uri: params.redirectUri,
    state: params.state,
  });
  return `${params.authorizeUrl}?${query.toString()}`;
}

function fragmentParams(hash: string): URLSearchParams | null {
  // Строго fragment: `URLSearchParams` одинаково разберёт и `?a=1`, и `#a=1`, а
  // разница здесь принципиальная. Код из query уехал бы на сервер, в логи прокси и
  // в Referer — то есть перестал бы быть одноразовым секретом. Подсунутый
  // `location.search` должен дать «кода нет», а не молча сработать.
  if (!hash.startsWith('#')) return null;
  return new URLSearchParams(hash.slice(1));
}

/**
 * Разбор `location.hash`. `null` — это не наш возврат (нет кода или нет state).
 *
 * Ошибку платформы (`#sso_error=…`) сюда не тащим: у неё свой смысл и свой текст,
 * а «нет кода» и «платформа отказала» — разные состояния экрана.
 */
export function readCallbackFragment(hash: string): SsoCallbackParams | null {
  const params = fragmentParams(hash);
  const code = params?.get('sso_code');
  const state = params?.get('state');
  if (!code || !state) return null;
  return { code, state };
}

export function readCallbackError(hash: string): string | null {
  return fragmentParams(hash)?.get('sso_error') ?? null;
}

/**
 * Схемы, по которым браузер исполняет или показывает содержимое в контексте текущей
 * страницы. Адрес возврата MCP-клиента задаёт сам клиент при регистрации, а переход
 * идёт с нашего origin, где лежат токены: `javascript:` здесь — это XSS.
 */
const UNSAFE_REDIRECT_SCHEMES = new Set(['javascript:', 'data:', 'vbscript:', 'file:', 'about:', 'blob:']);

/** Адрес возврата можно открыть: разбирается как URL и не несёт исполняемой схемы. */
export function isSafeClientRedirect(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  return !UNSAFE_REDIRECT_SCHEMES.has(parsed.protocol.toLowerCase());
}

/** `txn` MCP-входа из `state`, `null` — это не MCP-вход (или `txn` пуст). */
export function mcpTxnFromState(state: string | null | undefined): string | null {
  if (!state || !state.startsWith(MCP_STATE_PREFIX)) return null;
  const txn = state.slice(MCP_STATE_PREFIX.length);
  return txn || null;
}

/** `true`, если fragment несёт `state` MCP-входа — с кодом, ошибкой или вовсе пустой. */
export function isMcpCallback(hash: string): boolean {
  return mcpTxnFromState(fragmentParams(hash)?.get('state')) !== null;
}

/**
 * Разбор возврата MCP-входа. `null` — не MCP-вход либо платформа не прислала ни кода,
 * ни ошибки. Отказ платформы важнее кода: пришли оба — это аномалия, и завершать
 * вход по ней надо отказом, а не выдачей токенов.
 */
export function readMcpCallback(hash: string): McpCallbackParams | null {
  const params = fragmentParams(hash);
  const txn = mcpTxnFromState(params?.get('state'));
  if (!params || !txn) return null;
  const error = params.get('sso_error');
  if (error) return { txn, error };
  const code = params.get('sso_code');
  if (code) return { txn, code };
  return null;
}
